import { useLocalStorage } from "@uidotdev/usehooks";
import { useCallback, useContext, useMemo } from "react";
import { WorkspaceContext } from "../../../WorkspaceContext.tsx";
import {
    type StoredRecentlyViewed,
    addRecentlyViewed,
    getRecentlyViewedStorageKey,
    parseRecentlyViewed
} from "./recentlyViewed.ts";
import type { ProtectRecentlyViewedEntry, ProtectRecentlyViewedInput } from "./registerProtectSearchProvider.tsx";

export function useRecentlyViewed() {
    const workspaceContext = useContext(WorkspaceContext);
    const workspaceId = workspaceContext?.currentWorkspace?.id;

    const [stored, setStored] = useLocalStorage<StoredRecentlyViewed | undefined>(
        getRecentlyViewedStorageKey(workspaceId ?? "")
    );

    const entries = useMemo(() => parseRecentlyViewed(stored), [stored]);

    const record = useCallback(
        (entry: ProtectRecentlyViewedInput) => {
            if (!workspaceId) {
                return;
            }

            setStored(addRecentlyViewed(entries, { ...entry, viewedAt: Date.now() }));
        },
        [entries, setStored, workspaceId]
    );

    // Grouped once so each provider keeps the same array identity between renders and can memoize on it.
    const entriesByProviderId = useMemo(() => {
        const grouped = new Map<string, ProtectRecentlyViewedEntry[]>();

        entries.forEach(entry => {
            const providerEntries = grouped.get(entry.providerId);

            if (providerEntries) {
                providerEntries.push(entry);
            } else {
                grouped.set(entry.providerId, [entry]);
            }
        });

        return grouped;
    }, [entries]);

    return { entriesByProviderId, record };
}
